import React, { useState } from 'react'
import Menu, { MenuProps } from 'components/menu'
import { MOD_KEY, MenuItem, MenuList } from 'components/menu/items'
import { Copy, Cut, Paste } from 'components/icons'
import { useApplicationStore } from 'hooks/application'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { ICellValue, ITableViewColumn } from 'types'
import { formatValueForCopy, tsvEscape } from 'components/spreadsheet/helpers/format'
import { parseSingleCellPaste } from 'components/spreadsheet/helpers/paste'
import { isValidValue } from 'components/spreadsheet/helpers/validation'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'

interface FormCellMenuProps extends MenuProps {
  column: ITableViewColumn
  value: ICellValue
  formValues: Record<string, ICellValue>
  readOnly: boolean
  onChange: (value: ICellValue) => void
}

type TextBox = HTMLInputElement | HTMLTextAreaElement

// The highlighted text, or null when nothing is highlighted (or, in a number input, can't be).
const getSelectedText = (textBox: TextBox): string | null => {
  const { selectionStart: start, selectionEnd: end, value } = textBox
  return start !== null && end !== null && start !== end ? value.slice(start, end) : null
}

// Puts text where the highlighted text is (at the caret when nothing is, or over the whole value
// in an input with no caret to go by), as typing would.
const replaceSelectedText = (textBox: TextBox, text: string): void => {
  const { selectionStart: start, selectionEnd: end, value } = textBox
  const hasCaret = start !== null && end !== null
  const newValue = hasCaret ? value.slice(0, start) + text + value.slice(end) : text

  // React tracks the value through its own setter, so set it with the native one and fire the
  // input event for its onChange to pick up.
  Object.getOwnPropertyDescriptor(Object.getPrototypeOf(textBox), 'value')?.set?.call(textBox, newValue)
  textBox.dispatchEvent(new Event('input', { bubbles: true }))
  if (hasCaret) textBox.setSelectionRange(start + text.length, start + text.length)
  textBox.focus()
}

// The right-click menu on a form field. A form's values live in the form rather than in the
// table's rows, so this cuts, copies and pastes the field's own value instead of the grid's
// selection. Opened on a field being typed in, it works on the text box's text instead, as the
// text box's own menu would: the highlighted text, or all of it when nothing is highlighted.
const FormCellMenu: React.FC<FormCellMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  column,
  value,
  formValues,
  readOnly,
  onChange
}) => {
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const { spreadsheetData, selectedCell } = useDataContext()

  // The field's text box, when the menu was right-clicked from one being typed in. Caught as the
  // menu opens, while that text box still has focus.
  const [textBox] = useState<TextBox | null>(() => {
    const element = document.activeElement
    const typingInField = selectedCell.editing && selectedCell.columnId === column.publicId
    return typingInField && (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)
      ? element
      : null
  })

  const closeMenu = () => setMenuState(INITIAL_CONTEXT_MENU_STATE)

  const textToCopy = () =>
    textBox ? (getSelectedText(textBox) ?? textBox.value) : tsvEscape(formatValueForCopy(column, value))

  const handleCopy = async () => {
    closeMenu()
    try {
      await navigator.clipboard.writeText(textToCopy())
      setSnackbarMessage({ status: 'success', message: 'Copied to clipboard' })
    } catch (error) {
      displayErrorMessage(error)
    }
  }

  const handleCut = async () => {
    closeMenu()
    try {
      await navigator.clipboard.writeText(textToCopy())
      if (textBox) {
        if (getSelectedText(textBox) === null) textBox.select()
        replaceSelectedText(textBox, '')
      } else {
        onChange(null)
      }
      setSnackbarMessage({ status: 'success', message: 'Cut to clipboard' })
    } catch (error) {
      displayErrorMessage(error)
    }
  }

  const handlePaste = async () => {
    closeMenu()
    let text: string
    try {
      text = await navigator.clipboard.readText()
    } catch (error) {
      setSnackbarMessage({
        status: 'error',
        message: (error as Error)?.message ?? 'Unable to read from the clipboard.'
      })
      return
    }

    // Into a text box being typed in, the text goes in as typed, at the caret.
    if (textBox?.isConnected) {
      replaceSelectedText(textBox, text)
      return
    }

    let pastedValue: ICellValue
    try {
      pastedValue = parseSingleCellPaste(text, column)
    } catch (error) {
      // The paste helpers throw their messages as plain strings.
      setSnackbarMessage({ status: 'error', message: String(error) })
      return
    }

    // Refuse what the grid's paste refuses: a value that breaks the column's hard validation.
    const validation = isValidValue(
      pastedValue,
      null,
      spreadsheetData.columnValuesCount,
      column,
      { ...formValues, [column.publicId]: pastedValue },
      spreadsheetData.viewDetails.type,
      true
    )
    if (validation.error && !validation.warn) {
      setSnackbarMessage({ status: 'error', message: validation.errorMessage })
      return
    }

    onChange(pastedValue)
  }

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <div
        onMouseDown={(event) => {
          // Keep focus on the field the menu was opened from.
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        <MenuList>
          <MenuItem data-testid="form-cell-menu-copy" icon={<Copy />} shortcut={`${MOD_KEY}C`} onClick={handleCopy}>
            Copy value
          </MenuItem>
          <MenuItem
            data-testid="form-cell-menu-cut"
            icon={<Cut />}
            shortcut={`${MOD_KEY}X`}
            disabled={readOnly}
            onClick={handleCut}
          >
            Cut value
          </MenuItem>
          <MenuItem
            data-testid="form-cell-menu-paste"
            icon={<Paste />}
            shortcut={`${MOD_KEY}V`}
            disabled={readOnly}
            onClick={handlePaste}
          >
            Paste value
          </MenuItem>
        </MenuList>
      </div>
    </Menu>
  )
}

export default FormCellMenu
